import { ChangeEvent, useCallback, useMemo } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, FormControlLabel, Paper, Stack, Typography, useTheme } from "@mui/material";
import { Help } from "components/Help";
import NotFoundImg from "assets/images/not-found.png";
import { API_URL } from "consts";
import { useLazyImage } from "hooks/useLazyImage";
import type { IFamily } from "../types";
import getStyles from "./styles";

type CardProps = {
  family: IFamily;
  index: number;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: (value: string, index: number) => void;
  containerRef: React.RefObject<HTMLDivElement>;
};

const Card: React.FC<CardProps> = ({ family, index, isSelected, isDisabled, onSelect, containerRef }) => {
  const { t } = useTranslation();
  const { imgRef, src } = useLazyImage(family.image ? `${API_URL}${family.image}` : null, {
    containerRef,
    placeholder: NotFoundImg,
  });
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).Card, [theme]);

  const handleSelect = useCallback((ev: ChangeEvent<HTMLInputElement>) => {
    onSelect(ev.target.value, index);
  }, [index, onSelect]);

  return (
    <Paper sx={{ ...styles.container, ...(isDisabled && { opacity: 0.5 }) }}>
      <Stack sx={styles.image}>
        <Box component="img" ref={imgRef} src={src} alt={family.name} decoding="async" />
      </Stack>

      <Stack sx={styles.family}>
        <Typography variant="body2" fontWeight="bold" textAlign="center" px="var(--rhythm)">
          {family.name}
        </Typography>
      </Stack>

      <Stack component="section" sx={styles.section}>
        <Box component="small" dangerouslySetInnerHTML={{ __html: family.short_description }} sx={styles.description} />

        <Box sx={styles.tooltip}>{family.short_description && family.short_description.length > 55 && <Help text={family.short_description} />}</Box>

        <Stack sx={{ ...styles.control, background: isSelected ? "var(--color-primary)" : "rgba(242, 242, 242, 0.365)" }}>
          <FormControlLabel
            control={
              <Checkbox name="family_id" checked={isSelected} value={family.id} disabled={isDisabled} onChange={handleSelect} />
            }
            label={t("Select")}
            sx={styles.checkbox}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

export default Card;
